/**
 * safeRest.ts — Envoltura defensiva de `rest` (MSW v1).
 *
 * POR QUÉ: si un dato local quedó en una forma inesperada, el cuerpo de un
 * handler puede lanzar una excepción → la petición se rechaza → aparece la
 * pantalla fatal "No pudimos cargar" que solo se quitaba reinstalando.
 *
 * QUÉ HACE: ejecuta cada resolver dentro de try/catch.
 *   - GET: ante excepción responde 200 con `{ success: true, data: {} }` (la
 *     página se ve vacía, no se cae).
 *   - Mutaciones (POST/PUT/PATCH/DELETE): re-lanza el error (no fingimos éxito de
 *     un cobro/registro), pero deja log para diagnóstico.
 *
 * USO: en los handlers, `import { rest } from "@/mocks/safeRest"` en vez de "msw".
 */
/* eslint-disable @typescript-eslint/no-explicit-any -- infraestructura de mocks; tipado dinámico aceptable */
import { logger } from "@/lib/logger";
import { rest as mswRest } from "msw";

type AnyResolver = (req: any, res: any, ctx: any) => any;
type AnyPath = string | RegExp;

function pathOf(req: any): string {
  try {
    return req?.url?.pathname ?? String(req?.url ?? "");
  } catch {
    return "";
  }
}

/** GET: ante excepción devuelve 200 con envelope vacío (no tumbar parsers de servicios).
 * Si el resolver retorna undefined (passthrough guard), deja pasar la petición. */
function safeGetResolver(resolver: AnyResolver): AnyResolver {
  return async (req: any, res: any, ctx: any) => {
    try {
      const response = await resolver(req, res, ctx);
      // En MSW v1, si el resolver no retorna nada (o undefined),
      // se deja pasar la petición.
      if (response === undefined) return;
      return response;
    } catch (e) {
      logger.error("[MSW] GET falló; respuesta segura vacía:", pathOf(req), e);
      return res(
        ctx.status(200),
        ctx.json({
          success: true,
          data: {},
        }),
      );
    }
  };
}

/** Mutaciones: re-lanza el error (no fingir éxito), pero loguea.
 * Si el resolver retorna undefined (passthrough guard), deja pasar la petición. */
function loggedResolver(resolver: AnyResolver): AnyResolver {
  return async (req: any, res: any, ctx: any) => {
    try {
      const response = await resolver(req, res, ctx);
      if (response === undefined) return;
      return response;
    } catch (e) {
      logger.error("[MSW] handler falló:", pathOf(req), e);
      return res(
        ctx.status(500),
        ctx.json({
          success: false,
          message: "Internal Server Error (MSW)",
          error: String(e),
        }),
      );
    }
  };
}

export const rest = {
  get: (path: AnyPath, resolver: AnyResolver) =>
    mswRest.get(path as any, safeGetResolver(resolver) as any),
  post: (path: AnyPath, resolver: AnyResolver) =>
    mswRest.post(path as any, loggedResolver(resolver) as any),
  put: (path: AnyPath, resolver: AnyResolver) =>
    mswRest.put(path as any, loggedResolver(resolver) as any),
  patch: (path: AnyPath, resolver: AnyResolver) =>
    mswRest.patch(path as any, loggedResolver(resolver) as any),
  delete: (path: AnyPath, resolver: AnyResolver) =>
    mswRest.delete(path as any, loggedResolver(resolver) as any),
  options: (path: AnyPath, resolver: AnyResolver) =>
    mswRest.options(path as any, loggedResolver(resolver) as any),
  head: (path: AnyPath, resolver: AnyResolver) =>
    mswRest.head(path as any, loggedResolver(resolver) as any),
};
